/**
 * HTTP API contracts shared by the server (Fastify route schemas) and its clients (the
 * Admin, stations, e2e mocks). A change here is a change to the wire format: both sides
 * are type-checked against these schemas, so drift fails the build.
 * @module
 */
import { z } from 'zod';
import { permissionKeySchema } from '../permissions/index.js';
import { isCutoff, isValidTimeZone } from '../time/index.js';

// ---------------------------------------------------------------------------------------
// Errors

/**
 * Every error response: RFC 9457 problem details (`application/problem+json`) plus a
 * stable machine-readable `code`. Show `title` (and `detail` when present) to people;
 * branch on `code`. Unknown extra members are kept.
 */
export const problemSchema = z.looseObject({
  /** A URI for the problem type; `about:blank` when it is just the HTTP status. */
  type: z.string().optional(),
  /** Short, human-readable summary. */
  title: z.string(),
  /** The HTTP status code. */
  status: z.int().min(400).max(599),
  /** Stable machine-readable code, e.g. `not_a_member`, `validation_failed`. */
  code: z.string(),
  /** Human-readable explanation of this occurrence. */
  detail: z.string().optional(),
  /** Field problems when `code` is `validation_failed`. */
  errors: z.array(z.object({ path: z.string(), message: z.string().optional() })).optional(),
  /**
   * The server's id for this request, also sent as the {@link REQUEST_ID_HEADER} header on
   * every response. Quote it to support ("Error ref …"); the server's logs carry it too.
   */
  requestId: z.string().optional(),
});
/** A problem+json error body. */
export type Problem = z.infer<typeof problemSchema>;

/** The media type of error responses. */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

/**
 * Response header carrying the server's request id, on every response (errors too). Exposed
 * to browsers through CORS, so the Admin can show it as the error reference.
 */
export const REQUEST_ID_HEADER = 'x-request-id';

// ---------------------------------------------------------------------------------------
// Accounts

/** How an account handles VAT. */
export const VAT_MODES = ['registered', 'non_vat'] as const;
/** One of {@link VAT_MODES}. */
export type VatMode = (typeof VAT_MODES)[number];

const IANA_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

/**
 * True for an IANA time zone name this runtime knows (`Africa/Johannesburg`, `UTC`).
 * Offsets such as `+02:00` are refused: they have no daylight-saving rules.
 */
export function isIanaTimeZone(zone: string): boolean {
  return IANA_NAME.test(zone) && isValidTimeZone(zone);
}

/** An IANA time zone name. */
export const ianaTimeZoneSchema = z
  .string()
  .refine(isIanaTimeZone, { message: 'Must be an IANA time zone, e.g. Africa/Johannesburg' });

/** A trading-day cut-off, `HH:mm` (whole minutes, local time). */
export const cutoffSchema = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'Must be HH:mm')
  .refine(isCutoff, { message: 'Must be a time from 00:00 to 23:59' });

/**
 * The account time settings the trading day depends on (lesson 10). Validate with this
 * before writing them: the database stores whatever it is given.
 */
export const accountTimeSettingsSchema = z.object({
  timezone: ianaTimeZoneSchema,
  trading_day_cutoff: cutoffSchema,
});
/** Account time settings. */
export type AccountTimeSettings = z.infer<typeof accountTimeSettingsSchema>;

/** An account (tenant) as the API returns it. */
export const accountSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  timezone: ianaTimeZoneSchema,
  trading_day_cutoff: cutoffSchema,
  vat_mode: z.enum(VAT_MODES),
  /** ISO 4217, e.g. `ZAR`. */
  currency: z.string().regex(/^[A-Z]{3}$/),
});
/** An account. */
export type Account = z.infer<typeof accountSchema>;

// ---------------------------------------------------------------------------------------
// Admin identity

/** `GET /me`: who is signed in to the Admin, in which account, with which rights. */
export const meResponseSchema = z.object({
  user: z.object({
    id: z.uuid(),
    email: z.string(),
    display_name: z.string().nullable(),
  }),
  account: accountSchema,
  role: z.object({ id: z.uuid(), name: z.string().min(1) }),
  /** The branch the member is limited to, or null for all branches. */
  branch_scope: z.uuid().nullable(),
  permissions: z.array(permissionKeySchema),
});
/** Response of `GET /me`. */
export type MeResponse = z.infer<typeof meResponseSchema>;

// ---------------------------------------------------------------------------------------
// Local development

/** Header that lets a non-loopback client use `/dev/*` routes in local development. */
export const DEV_ACCESS_HEADER = 'x-enp-dev-code';

/** `POST /dev/login` body (local development only). */
export const devLoginRequestSchema = z.object({ email: z.email().max(254) });

/** `POST /dev/login` response. */
export const devLoginResponseSchema = z.object({
  token: z.string().min(1),
  expires_at: z.iso.datetime({ offset: true }),
  admin_user_id: z.uuid(),
  account_id: z.uuid(),
});
/** Response of `POST /dev/login`. */
export type DevLoginResponse = z.infer<typeof devLoginResponseSchema>;

// ---------------------------------------------------------------------------------------
// Catalogue (read API)

export * from './catalogue.js';

// ---------------------------------------------------------------------------------------
// Catalogue import

export * from './imports.js';

// ---------------------------------------------------------------------------------------
// Stock and sales (read API, projection repair)

export * from './stock.js';

// ---------------------------------------------------------------------------------------
// Sales replay

export * from './replay.js';

// ---------------------------------------------------------------------------------------
// Stations, devices and held station actions

export * from './devices.js';

// ---------------------------------------------------------------------------------------
// Stock control: approvals, counts, write-offs, the daily state list, settings

export * from './stock-control.js';

// ---------------------------------------------------------------------------------------
// Production: recipes, cost roll-up, batches, reports; catalogue edits and field ownership

export * from './production.js';

// ---------------------------------------------------------------------------------------
// Purchasing: suppliers, price lists, requests, orders, receipts, restock suggestion

export * from './purchasing.js';

// ---------------------------------------------------------------------------------------
// Feasibility and commitment coverage: the planning board

export * from './feasibility.js';

// ---------------------------------------------------------------------------------------
// Customer orders (custom, wholesale) and allocating batch output to them

export * from './orders.js';

// ---------------------------------------------------------------------------------------
// Staff, station PINs and roles

export * from './staff.js';
